import { Button, CircularProgress, IconButton, Stack, Tooltip } from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import { useState, useRef, useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { projectsApi } from '../../../api/projectsApi';
import { applicationSlice } from '../../../redux/ApplicationSlice';
import { projectSlice } from '../../../redux/ProjectSlice';
import { useAppDispatch, useAppSelector } from '../../../redux/ReduxHooks';
import { ApplicationActions } from '../../../utils/constants';
import { isBrowserOffline } from '../../../utils/networkStatus';
import { pickProjectVersionFields } from '../../../utils/projectVersions';
import { stableJsonSignature } from '../../../utils/stableJsonSignature';
import {
  COLLABORATOR_SYNC_ERROR_SNACKBAR_ID,
  setCollaboratorSyncError,
  useCollaboratorSyncError,
} from '../../Snackbars/collaboratorSyncErrorNotification';
import type { Project } from '../../Projects/projects';

interface SyncRequestIdentity {
  accessLevel: Project['accessLevel'];
  pin: string;
  projectId: string;
  projectPayloadSignature: string;
  requestId: number;
  shareCode: string;
}

type CurrentSyncProjectIdentity = Pick<SyncRequestIdentity, 'accessLevel' | 'pin' | 'projectId' | 'projectPayloadSignature' | 'shareCode'>;

const getProjectSyncPayload = (project: Project) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { pin, accessLevel, shareCode, isCollaborative, collaborators, ...projectToSync } = project;
  return projectToSync;
};

const getProjectSyncPayloadSignature = (project: Project) => stableJsonSignature(getProjectSyncPayload(project));

const BottomActionButtons = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const containerRef = useRef<HTMLDivElement>(null);

  // Track container width to handle drawer resizing
  const [containerWidth, setContainerWidth] = useState<number>(Infinity);

  // Switch to icon-only mode when container is narrower than threshold
  // 400px for viewer mode (2 buttons), 200px for collaborator mode (1 button)
  const isSmallContainer = containerWidth < 400;

  // Get project info from Redux
  const project = useAppSelector((state) => state.project.loadedProject);
  const accessLevel = useAppSelector((state) => state.project.loadedProject?.accessLevel);
  const currentProjectSyncPayloadSignature = project ? getProjectSyncPayloadSignature(project) : null;
  const currentSyncProjectIdentity: CurrentSyncProjectIdentity | null =
    project && currentProjectSyncPayloadSignature
      ? {
          accessLevel,
          pin: project.pin ?? '',
          projectId: project.id,
          projectPayloadSignature: currentProjectSyncPayloadSignature,
          shareCode: project.shareCode ?? '',
        }
      : null;

  // Sync state
  const [isSyncing, setIsSyncing] = useState(false);
  const syncError = useCollaboratorSyncError();
  const activeSyncRequestRef = useRef<SyncRequestIdentity | null>(null);
  const currentSyncProjectIdentityRef = useRef<CurrentSyncProjectIdentity | null>(null);
  const nextSyncRequestIdRef = useRef(0);
  currentSyncProjectIdentityRef.current = currentSyncProjectIdentity;

  const isSameSyncRequest = (request: SyncRequestIdentity) => {
    const activeRequest = activeSyncRequestRef.current;

    return (
      activeRequest?.requestId === request.requestId &&
      activeRequest.accessLevel === request.accessLevel &&
      activeRequest.projectId === request.projectId &&
      activeRequest.shareCode === request.shareCode &&
      activeRequest.pin === request.pin &&
      activeRequest.projectPayloadSignature === request.projectPayloadSignature
    );
  };

  const isActiveSyncRequest = (request: SyncRequestIdentity) => {
    const currentIdentity = currentSyncProjectIdentityRef.current;

    if (!isSameSyncRequest(request) || !currentIdentity) {
      return false;
    }

    return (
      currentIdentity.accessLevel === request.accessLevel &&
      currentIdentity.projectId === request.projectId &&
      currentIdentity.shareCode === request.shareCode &&
      currentIdentity.pin === request.pin &&
      currentIdentity.projectPayloadSignature === request.projectPayloadSignature
    );
  };

  // Observe container width changes to handle drawer resizing
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    activeSyncRequestRef.current = null;
    setIsSyncing(false);
    setCollaboratorSyncError(null);
  }, [accessLevel, currentProjectSyncPayloadSignature, project?.id, project?.pin, project?.shareCode]);

  useEffect(
    () => () => {
      activeSyncRequestRef.current = null;
      setCollaboratorSyncError(null);
    },
    []
  );

  const handleSyncToPlatform = async () => {
    if (!project?.shareCode || !project?.pin) {
      console.error('Missing shareCode or PIN');
      return;
    }

    if (accessLevel !== 'collaborator') {
      return;
    }

    if (isBrowserOffline()) {
      setCollaboratorSyncError(t('primaryBar.error.sync-offline', 'You appear to be offline. Connect to the internet to sync this project.'));
      return;
    }

    const projectToSync = getProjectSyncPayload(project);
    const projectPayloadSignature = stableJsonSignature(projectToSync);

    const syncRequest = {
      accessLevel,
      pin: project.pin,
      projectId: project.id,
      projectPayloadSignature,
      requestId: (nextSyncRequestIdRef.current += 1),
      shareCode: project.shareCode,
    };

    currentSyncProjectIdentityRef.current = syncRequest;
    activeSyncRequestRef.current = syncRequest;
    setIsSyncing(true);
    setCollaboratorSyncError(null);

    try {
      // Serialize project without client-side fields
      const projectString = JSON.stringify(projectToSync);

      // Call API to update on platform
      const response = await projectsApi.update(project.shareCode, project.pin, projectString, project.name);
      const versionFields = pickProjectVersionFields(response);
      if (isActiveSyncRequest(syncRequest) && Object.keys(versionFields).length > 0) {
        dispatch(
          projectSlice.actions.onUpdateProjectApiMetadata({
            projectId: project.id,
            ...versionFields,
          })
        );
      }
    } catch (error) {
      console.error('Failed to sync project:', error);
      if (isActiveSyncRequest(syncRequest)) {
        setCollaboratorSyncError(t('primaryBar.error.sync-general', 'Failed to sync project. Please check your connection and try again.'));
      }
    } finally {
      if (isSameSyncRequest(syncRequest)) {
        activeSyncRequestRef.current = null;
        setIsSyncing(false);
      }
    }
  };

  const handleCopyAndEdit = () => {
    if (!project) return;
    dispatch(
      applicationSlice.actions.onOpenDialog({
        ...ApplicationActions.Dialogs.PROJECT_NAME,
        options: {
          ...ApplicationActions.Dialogs.PROJECT_NAME.options,
          title: 'projectNameDialog.new.title',
          copyFromProjectId: project.id,
        },
      })
    );
  };

  const handleOpenEditPin = () => {
    dispatch(applicationSlice.actions.onOpenDialog(ApplicationActions.Dialogs.EDIT_PIN_SHARE_CODE));
  };

  // Show sync button for collaborators
  if (accessLevel === 'collaborator' && project?.shareCode && project?.pin) {
    const syncTooltip =
      syncError || (isSyncing ? t('primaryBar.button.syncing', 'Syncing...') : t('primaryBar.button.sync-to-platform', 'Sync to Platform'));
    const syncLabel = t('primaryBar.button.sync-to-platform', 'Sync to Platform');

    if (isSmallContainer) {
      // Icon-only view for small containers (accounts for drawer width)
      return (
        <Stack ref={containerRef} direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ flex: 1, minWidth: 0 }}>
          <Tooltip title={syncTooltip}>
            <span>
              <IconButton
                onClick={handleSyncToPlatform}
                disabled={isSyncing}
                size="medium"
                aria-label={syncLabel}
                aria-describedby={syncError ? COLLABORATOR_SYNC_ERROR_SNACKBAR_ID : undefined}
                sx={syncError ? { color: 'error.main' } : {}}
              >
                {isSyncing ? <CircularProgress size={24} /> : <SyncIcon />}
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      );
    }

    // Full button view for larger containers
    return (
      <Stack ref={containerRef} direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ flex: 1, minWidth: 0 }}>
        <Button
          variant="contained"
          startIcon={isSyncing ? <CircularProgress size={16} /> : <SyncIcon />}
          onClick={handleSyncToPlatform}
          disabled={isSyncing}
          aria-describedby={syncError ? COLLABORATOR_SYNC_ERROR_SNACKBAR_ID : undefined}
          color={syncError ? 'error' : 'primary'}
          sx={{
            borderRadius: '8px',
            height: 36,
            minWidth: 140,
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {isSyncing ? (
            <Trans i18nKey="primaryBar.button.syncing">Syncing...</Trans>
          ) : (
            <Trans i18nKey="primaryBar.button.sync-to-platform">Sync to Platform</Trans>
          )}
        </Button>
      </Stack>
    );
  }

  // Show read-only controls for viewers
  if (accessLevel === 'viewer') {
    if (isSmallContainer) {
      // Icon-only view for small containers (accounts for drawer width)
      return (
        <Stack ref={containerRef} direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
          <Tooltip title={t('primaryBar.button.copy-and-edit', 'Copy and Edit')}>
            <IconButton onClick={handleCopyAndEdit} size="medium">
              <ContentCopyIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title={t('primaryBar.button.enter-collaborator-pin', 'Enter Collaborator PIN')}>
            <IconButton onClick={handleOpenEditPin} size="medium">
              <VpnKeyIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      );
    }

    // Full button view for larger containers
    return (
      <Stack ref={containerRef} direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ flex: 1 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleCopyAndEdit}
          startIcon={<ContentCopyIcon />}
          sx={{
            borderRadius: '8px',
            height: 36,
            minWidth: 120,
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Trans i18nKey="primaryBar.button.copy-and-edit">Copy and Edit</Trans>
        </Button>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleOpenEditPin}
          startIcon={<VpnKeyIcon />}
          sx={{
            borderRadius: '8px',
            height: 36,
            minWidth: 160,
            textTransform: 'none',
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          <Trans i18nKey="primaryBar.button.enter-collaborator-pin">Enter Collaborator PIN</Trans>
        </Button>
      </Stack>
    );
  }

  // No special controls for local projects
  return null;
};

export default BottomActionButtons;
